import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, BehaviorSubject, catchError, defer, from, lastValueFrom, map, Observable, of, share, Subject, Subscription, switchMap, tap, throwError } from 'rxjs';

import { NgxPermissionsService } from 'ngx-permissions';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { AuthCognitoService, SsoSignInFailure } from './auth-cognito.service';
import { AuthSessionCleanupService } from './auth-session-cleanup.service';
import {
  deleteLegacyCookies,
  externalURLSamePageWithoutHistory,
  getAccessTokenPayloadInfo,
  getCookie,
  setCookie,
  signinLegacyApi,
  updateActionPlanAPIUrlCookie,
  updateAdministrationAPIUrlCookie,
  updateAuthenticationAPIUrlCookie,
  updateBookmarkAPIUrlCookie,
  updateChecklistAPIUrlCookie,
  updateCilAPIUrlCookie,
  updateCloudFrontKeyPairIdCookie,
  updateCloudFrontPolicyCookie,
  updateCloudFrontSignatureCookie,
  updateCommentManagerAPIUrlCookie,
  updateCommentsAPIUrlCookie,
  updateCopilotAPIUrlCookie,
  updateDateFormatCookie,
  updateDefectTagAPIUrlCookie,
  updateDocumentAPIUrlCookie,
  updateEnvironmentFullURLCookie,
  updateHashtagAPIUrlCookie,
  updateIdleTimeoutCookie,
  updateIntegrationAPITokenCookie,
  updateIntegrationAPIURLCookie,
  updateLanguageIdCookie,
  updateLastSiteIdCookie,
  updateMultilingualAPIUrlCookie,
  updateSubtitleAPIUrlCookie,
  updateNotificationAPIUrlCookie,
  updatePraiseAPIUrlCookie,
  updateProductAPITokenCookie,
  updateReactionAPIUrlCookie,
  updateReloadSessionCookie,
  updateUserIdCookie,
  syncLegacySessionCookies as applyLegacySessionCookies,
  SvcAppSettings,
  SvcModule,
  updateModuleIntegrationAPIUrlCookie,
  isNullOrUndefined,
} from 'projects/lib-shared-common/src/public-api';
import { CloudfrontService } from './legacy/cloudfront.service';
import { AuthStatus, UserStatusAuth } from './model/user-auth.model';
import { AcessSub, CognitoUser, EnvironmentInfo, EnvironmentUserInfo, SsoItem, TokenRefresh, UserSignInFailedResponse } from './model/environment.info.model';
import { AWS_DEFAULT_BACKGROUND_IMAGE, AWS_DEFAULT_LOGO_SOLVACE } from '../Constants/AWS-IMAGES';
import { AWSAmplifyAuthResult } from './model/aws-amplify-auth-info';
import { User } from '../features/user/services/user.types';
import { AuthUtils } from './auth.utils';
import {
  tryToRedirectToAspFromQueryParamData,
  tryToRedirectToCoreFromQueryParamData,
  canRedirectToAspFromQueryParamData,
  canRedirectToCoreFromQueryParamData,
} from './auth-functions';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'projects/environments/environment';
import { SvcDataCacheService } from '../services/cache/svc-data-cache.service';
import { ParameterService } from '../services/parameter/parameter.service';
import { CloudfrontCookie } from './model/cloudfront-cookie.mode';
import { SvcStoredAuthUserService } from './svc-stored-auth-user.service';
import { SvcStoredAuthUser } from './model/svc-stored-auth-user.interface';
import { SvcCacheName } from '../services/cache/svc-cache-name.enum';
import { takeMcpAuthorizationRedirect } from './mcp-authorization-redirect';
import { ADMIN_PERMISSION } from './auth-permissions.constants';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _signIn$ = new Subject<{ success: boolean }>();
  private _signOut$ = new Subject<{ success: boolean }>();
  public signIn$ = this._signIn$.asObservable();
  public signOut$ = this._signOut$.asObservable();
  public ssoSignInFailure$: Observable<SsoSignInFailure | undefined>;
  public ssoSignInSuccess$: Observable<void>;

  private _authenticated: boolean = false;
  private _changePasswordRequired: boolean = false;
  private _emailToResetPassword: string;
  private _environmentURL: string;
  private _environmentInfo: EnvironmentInfo;
  private _userStatusAuth: UserStatusAuth = {} as UserStatusAuth;

  public solvaceLogo$ = new BehaviorSubject<string>('');

  private readonly _failedCoverImages = new Set<string>();
  private _probedCoverImage: string | null = null;
  private readonly _coverImageFallback$ = new Subject<void>();
  /** Emits when the configured login background fails to load and `getCoverImage()` switches to the bundled default. */
  public readonly coverImageFallback$ = this._coverImageFallback$.asObservable();

  public getAuthStatusFn: () => Observable<AuthStatus>;

  private readonly _tokenRefresh$: Observable<string> = defer(() => this._httpClient.get<TokenRefresh>(`${this._appConfig.APIs.apiUrlAuth}/Token/refresh`)).pipe(
    map((response) => response.refreshToken),
    share()
  );

  /**
   * Constructor
   */
  constructor(
    private _httpClient: HttpClient,
    private _appConfig: AppEnvironmentConfig,
    private _permissionsService: NgxPermissionsService,
    private _authCognitoService: AuthCognitoService,
    private _parameterService: ParameterService,
    private _cloudfrontService: CloudfrontService,
    private _activatedRoute: ActivatedRoute,
    private _svcAppSettings: SvcAppSettings,
    private _router: Router,
    private _dataCacheService: SvcDataCacheService,
    private _storedAuthUserService: SvcStoredAuthUserService,
    private _authSessionCleanupService: AuthSessionCleanupService
  ) {
    this._dataCacheService.initializeFromAuth(this.signIn$, this.signOut$);
    this.ssoSignInFailure$ = this._authCognitoService.ssoSignInFailure$;
    this.ssoSignInSuccess$ = this._authCognitoService.ssoSignInSuccess$;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get isFeedModule(): boolean {
    return [SvcModule.Feed].includes(this._svcAppSettings.module);
  }

  get isEmbeddedModule(): boolean {
    return [SvcModule.Notification, SvcModule.ExternalFeatures].includes(this._svcAppSettings.module);
  }

  /**
   * Setter & getter for access token
   */
  set accessToken(token: string) {
    if (environment.isLocalhost || this.isFeedModule) {
      localStorage.setItem('accessToken', token?.replace("''", ''));
    }
    setCookie('EE3B69BC-D275-4D89-ACCD-D91136DAD45C', token?.replace("''", ''));
  }

  get accessToken(): string {
    if (environment.isLocalhost || this.isFeedModule) return localStorage.getItem('accessToken');
    return (getCookie('EE3B69BC-D275-4D89-ACCD-D91136DAD45C') || '').replace("''", '');
  }

  get environmentURL(): string {
    return this._environmentURL;
  }

  get userId(): string {
    return getAccessTokenPayloadInfo('UserId');
  }

  set userStatusAuth(userStatusAuth: UserStatusAuth) {
    this._userStatusAuth = userStatusAuth;
  }

  get userStatusAuth(): UserStatusAuth {
    return this._userStatusAuth;
  }

  // Maps the browser origin (localhost) to the expected real environment URL.
  // This is needed because the backend uses `environmentUrl` to return the correct Cognito/SSO configuration.
  // When running locally at `http://localhost:4200`, we derive the target environment from the current build
  // (via `environment` flags and `environment.APIs.apiUrlAuth`) so that login works against the right backend.
  set environmentURL(value: string) {
    if (value.startsWith('http://localhost:4200')) {
      const apiAuthUrl = environment?.APIs?.apiUrlAuth ?? '';

      // QA front-end uses dev APIs; `isQA && !isDEV` is set by `environment.qa.ts` (`ng serve -c qa`).
      if (environment.isQA && !environment.isDEV) {
        this._environmentURL = 'https://qa.solvacelabs.com';
      } else if (apiAuthUrl.includes('-qa.')) {
        this._environmentURL = 'https://qa.solvacelabs.com';
      } else if (apiAuthUrl.includes('-rc.') || apiAuthUrl.includes('-rv.')) {
        this._environmentURL = 'https://sandboxefeso.solvacelabs.com';
      } else if (apiAuthUrl.includes('-rt.')) {
        this._environmentURL = 'https://sandboxrt.solvacelabs.com';
      } else if (apiAuthUrl.includes('-edge.')) {
        this._environmentURL = 'https://sandboxrevamp.solvacelabs.com';
      } else {
        this._environmentURL = 'https://devsp.solvacelabs.com';
      }
    } else {
      this._environmentURL = value;
    }
    this._authCognitoService.setEnvironmentURL(this.environmentURL);
  }

  get passwordExpirationDays(): number {
    return Number.parseInt(localStorage.getItem('passwordExpirationDays')) || 90;
  }

  get changePasswordRequired(): boolean {
    return this._changePasswordRequired;
  }

  get environmentInfo(): EnvironmentInfo {
    return this._environmentInfo;
  }

  get authenticated() {
    return this._authenticated;
  }

  get needToRedirectToOldVersion() {
    const cookies = document.cookie.split(';').map((cookieStr) => {
      const splitedCookie = cookieStr.trim().split('=');
      return {
        name: splitedCookie[0],
        value: splitedCookie[1],
      };
    });
    const cookieName = `NEWVER-ACTIVE-{${this.userId}}`;
    for (const cookie of cookies) {
      if (cookie.name == cookieName) {
        return cookie.value === 'false';
      }
    }
    return true;
  }

  checkAuthenticatedProperty() {
    if (!this._authenticated) {
      this._authenticated = !!this.accessToken;
    }
    if (this._authenticated && !Object.keys(this._permissionsService.getPermissions()).length) {
      this.restoreStoredRoles();
    }
  }

  /**
   * Reloads NgxPermissions for restored sessions (page refresh / returning user),
   * where the interactive sign-in flow that calls `loadPermissions` did not run.
   * Falls back to ADMIN because the sign-in flow currently grants it to every user
   * (see `postLoginSub` usage) — replace both spots once a real roles API exists.
   */
  private restoreStoredRoles(): void {
    let roles: string[] | null = null;
    try {
      roles = JSON.parse(localStorage.getItem('roles'));
    } catch {
      roles = null;
    }
    this._permissionsService.loadPermissions(roles?.length ? roles : [ADMIN_PERMISSION]);
  }

  startChangePasswordRequired(): void {
    this._changePasswordRequired = true;
  }
  resetChangePasswordRequired(): void {
    this._changePasswordRequired = false;
  }

  applyCongnitoConfig(env: EnvironmentInfo) {
    env && this._authCognitoService.applyCongnitoConfig(env);
  }

  ssoSignIn(sso: SsoItem, options?: { forceLogin?: boolean }): Promise<void> {
    return this._authCognitoService.ssoSignIn(sso, options);
  }

  getCurrentUser(): Promise<CognitoUser | null> {
    return this._authCognitoService.getCurrentUser();
  }

  getCoverImage(): string {
    const configured = this._environmentInfo?.backgroundImage;
    if (!configured || this._failedCoverImages.has(configured)) {
      return `url(${AWS_DEFAULT_BACKGROUND_IMAGE})`;
    }
    this.probeCoverImage(configured);
    return `url(${configured})`;
  }

  private probeCoverImage(src: string): void {
    if (this._probedCoverImage === src) return;
    this._probedCoverImage = src;
    const probe = new Image();
    probe.onerror = () => {
      this._failedCoverImages.add(src);
      this._coverImageFallback$.next();
    };
    probe.src = src;
  }

  getIsGlobalUrl() {
    return localStorage.getItem('authFlowGlobal_environment') !== null;
  }

  postEnvironmentInfo(urlEnv?: string): Observable<EnvironmentInfo> {
    if (this._environmentInfo) {
      this.solvaceLogo$.next(AWS_DEFAULT_LOGO_SOLVACE);
      return of(this._environmentInfo);
    }
    const url = `${this._appConfig.APIs.apiUrlAuth}/Environment/info`;
    return this._httpClient
      .post<EnvironmentInfo>(url, {
        environmentUrl: this.getEnvironmentUrlCurrent(urlEnv),
      })
      .pipe(
        tap((info) => {
          this._environmentInfo = info;
          this.solvaceLogo$.next(AWS_DEFAULT_LOGO_SOLVACE);
        }),
        catchError(() => {
          this._environmentInfo = null;
          return of(this._environmentInfo);
        })
      );
  }

  postEnvironmentListByUserName(userName: string) {
    const url = `${this._appConfig.APIs.apiUrlAuth}/Environment/username`;

    return lastValueFrom(
      this._httpClient.post<EnvironmentUserInfo[]>(url, {
        userName: userName,
      })
    );
  }

  logSignInFailedAttempt(userName: string, urlEnv: string, errorDetail?: string, errorUrl?: string): Promise<UserSignInFailedResponse> {
    const url = `${this._appConfig.APIs.apiUrlAuth}/AccessLog?userName=${userName}&environmentUrl=${urlEnv}`;
    return lastValueFrom(this._httpClient.post<UserSignInFailedResponse>(url, { errorDetail, errorUrl }));
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get environment url current
   *
   * @param urlEnv
   */
  public getEnvironmentUrlCurrent(urlEnv: string): string {
    this.environmentURL = urlEnv || document.location.origin;
    return this.environmentURL;
  }

  /**
   * Forgot password
   *
   * @param email
   */
  forgotPassword(email: string): Promise<any> {
    this._emailToResetPassword = email;
    return this._authCognitoService.sendVerificationCode(email);
  }

  /**
   * Reset password
   *
   * @param password
   */
  async resetPassword(code: string, password: string, options?: { updatePasswordChangeDate?: boolean }): Promise<CognitoUser> {
    const username = this._emailToResetPassword;
    await this._authCognitoService.resetPasswordWithVerificationCode(code, username, password);
    if (options?.updatePasswordChangeDate ?? true) {
      await this.setPasswordChangeDate();
    }
    let user = await this._authCognitoService.getCurrentUser();
    user = <CognitoUser>{
      ...(user ?? {}),
      username,
    };
    return user;
  }

  async changeTemporaryPassword(password: string, options?: { updatePasswordChangeDate?: boolean }): Promise<CognitoUser> {
    await this._authCognitoService.changeTemporaryPassword(password);
    if (options?.updatePasswordChangeDate ?? true) {
      await this.setPasswordChangeDate();
    }
    // AB#73695 - must settle before signOut() revokes the token this fetch depends on.
    const user = await this._authCognitoService.getCurrentUser();
    await this._authCognitoService.signOut();
    // AB#75346 - signOut() only revokes the session server-side (direct GlobalSignOut REST call),
    // it never clears Amplify's own local Cognito/amplify- cache. Left uncleared, Amplify's
    // internal signIn() still finds that stale local session on the very next sign-in attempt and
    // throws "There is already a signed in user.", which raiseLoginError() doesn't recognize and
    // shows as "Usuário ou senha inválidos" - hiding the real cause from both the user and the logs.
    this.clearCognitoLocalStorage();
    return user;
  }

  changePassword(oldPassword: string, newPassword: string, options?: { updatePasswordChangeDate?: boolean }): Promise<CognitoUser> {
    return new Promise<CognitoUser>(async (resolve, reject) => {
      let user: CognitoUser;
      this.prepareConfigFromEnvironmentInfo()
        .pipe(
          switchMap(() => from(this._authCognitoService.getCurrentUser()).pipe(tap((currentUser) => (user = currentUser)))),
          switchMap(() => from(this._authCognitoService.changePassword(oldPassword, newPassword))),
          switchMap(() => {
            if (options?.updatePasswordChangeDate ?? true) {
              return from(this.setPasswordChangeDate());
            }
            return of();
          }),
          tap(() => resolve(user)),
          catchError((e) => {
            reject(e);
            return EMPTY;
          })
        )
        .subscribe();
    });
  }

  prepareConfigFromEnvironmentInfo(): Observable<void> {
    return this.postEnvironmentInfo().pipe(
      tap((c) => this.applyCongnitoConfig(c)),
      switchMap(() => of(null))
    );
  }

  /**
   * Sign in
   *
   * @param credentials
   */
  signIn(
    credentials: {
      user: string;
      password: string;
    },
    urlEnv: string
  ): Promise<UserStatusAuth> {
    if (this._authenticated) {
      throwError(() => new Error('User is already logged in.'));
    }

    return this._authCognitoService.signIn(credentials).then((response) => {
      if (response.result === AWSAmplifyAuthResult.Success) {
        return this.loginValidate(urlEnv);
      } else if (response.result === AWSAmplifyAuthResult.NewPasswordRequired) {
        this.startChangePasswordRequired();
        return {
          user: null,
          status: AuthStatus.TemporaryPasswordUser,
        };
      }
    });
  }

  signInCurrentUser(urlEnv: string, trustSub = true): Promise<UserStatusAuth> {
    return this.loginValidate(urlEnv, undefined, true, trustSub);
  }

  refreshApiToken(): Promise<string> {
    const responseError = (message: string) => {
      return new HttpErrorResponse({ status: 401, statusText: 'Unauthorized', error: message });
    };
    return new Promise<string>(async (resolve, reject) => {
      if (!this._authCognitoService.authenticatedUserSub) return reject(responseError('Failed to get sub'));

      try {
        const newToken = await this.postLoginSub(getAccessTokenPayloadInfo('iss'), this._authCognitoService.authenticatedUserToken, this._authCognitoService.authenticatedUserSub);
        if (!newToken) return reject(responseError('Failed to renew token'));
        this.accessToken = newToken.accessToken;
        this._authenticated = true;
        applyLegacySessionCookies(newToken.accessToken);
        resolve(newToken.accessToken);
      } catch (err) {
        // A token-refresh failure is incidental, not an intentional "log out completely" action:
        // only end this app's local session, don't cascade a global Cognito/IdP revoke.
        await lastValueFrom(this.signOut(true, { revokeRemoteSession: false }));
        return reject(responseError(err instanceof Error ? err.message : 'Erro desconhecido'));
      }
    });
  }

  public signInStoredUserAuth(userId: string): Promise<UserStatusAuth> {
    const user = this._storedAuthUserService.getUserLoggedByUserId(userId);
    const url = this._dataCacheService.get<EnvironmentInfo>?.(SvcCacheName.ENVIRONMENT_INFO)?.url;
    return this.loginValidate(url, user, true);
  }

  public signOutStoredUserAuth(userId: string): void {
    this._authSessionCleanupService.clear({ storedAuthUsers: userId });
    // Removing one stored quick sign-in entry doesn't warrant revoking the corporate IdP session.
    this.signOut(true, { revokeRemoteSession: false });
  }

  private async loginValidate(
    urlEnv: string,
    storedAuthUser?: SvcStoredAuthUser,
    bypassAccessTokenBlock = false,
    trustSub = bypassAccessTokenBlock
  ): Promise<UserStatusAuth> {
    try {
      const userStatus = {} as UserStatusAuth;

      // Quick sign-in can run outside the sign-in page, where Amplify is never configured -
      // getCurrentUser() below would otherwise always resolve null and starve the live-session fallback.
      if (bypassAccessTokenBlock && !this._authCognitoService.hasCognitoConfig()) {
        await lastValueFrom(this.prepareConfigFromEnvironmentInfo());
      }

      const currentUser = await this.getCurrentUser();

      // Identity always comes from the TARGET account, never the active session -
      // getCurrentUser() here only supplies a fresh token (direct use or live-session fallback).
      userStatus.user = storedAuthUser
        ? ({
            accessToken: storedAuthUser.cognitoAccessToken,
            baseUrl: storedAuthUser.environmentUrl,
            email: storedAuthUser.userName,
            fullName: storedAuthUser.fullName,
            firstName: storedAuthUser.fullName,
            ssoId: storedAuthUser.cognitoSsoId,
            ssoUsername: storedAuthUser.ssoUsername ?? storedAuthUser.userName,
            username: storedAuthUser.userName,
          } as CognitoUser)
        : currentUser;

      const sub = await this.postLoginSubWithLiveSessionFallback(
        urlEnv,
        userStatus?.user?.accessToken,
        userStatus?.user?.ssoId,
        userStatus?.user?.ssoUsername,
        bypassAccessTokenBlock,
        currentUser?.accessToken,
        trustSub
      );

      if (sub) {
        // Store the access token in the local storage
        this.accessToken = sub.accessToken;

        // Set and store user roles
        const roles = [ADMIN_PERMISSION];
        this._permissionsService.loadPermissions(roles);
        localStorage.setItem('roles', JSON.stringify(roles));

        this._storedAuthUserService.addStoredAuthUser(
          userStatus?.user?.accessToken,
          userStatus?.user?.ssoId,
          userStatus?.user?.fullName,
          urlEnv,
          // AB#75346 - true federation signal only, never backfilled from the username.
          storedAuthUser ? storedAuthUser.ssoUsername : currentUser?.ssoUsername
        );

        const status = await this.getUserStatusAuth();
        if (isNullOrUndefined(status)) throw new Error('The user status cant be caught from GET "/infos/cached" endpoint.');
        userStatus.status = status;

        // AB#69653 - AuthGuard/check() gate route access solely on `_authenticated`, and check()
        // resolves synchronously (`if (this._authenticated) return of(true);`) with no await. Setting
        // this flag as soon as /Login/sub succeeded (before the status fetch above) let a guard
        // re-check that landed in that window let the router into a private route while this method
        // could still end up throwing/returning null - MyWorkspace would flash in, then AuthCheckService
        // or the sign-in component's own catch (which calls signOut()) would kill it moments later.
        // Only flip to authenticated once the whole validation, including the status fetch, succeeded.
        this._authenticated = true;

        // Set user status
        this._signIn$.next({ success: true });
      } else {
        // Set user status
        userStatus.status = AuthStatus.NotFoundUser;

        // The backend rejected this sub: invalidate OUR OWN caches that could have produced a stale
        // one (authenticatedUserSub/Token bookkeeping + the quick sign-in entry), so a retry re-reads
        // a fresh session from Cognito instead of reusing the same stale sub.
        //
        // Deliberately NOT clearing the live Cognito/Amplify session (cognitoTokens) here: getCurrentUser()
        // is tried before this branch is ever reached, so when a real Amplify session already returned
        // this sub, NotFoundUser legitimately means "new federated user, not yet provisioned" - the
        // user-locked/new -> /UserFederated -> user-locked/waiting flow depends on that same Cognito
        // session surviving until the post-approval re-login. Force-clearing it here would break that.
        this._authSessionCleanupService.clear({
          authenticatedUserSub: true,
          storedAuthUsers: storedAuthUser?.userId,
        });
      }

      this.userStatusAuth = userStatus;
      return userStatus;
    } catch (err) {
      if (!bypassAccessTokenBlock && err instanceof HttpErrorResponse && err.status === 400 && err.error?.errors?.AccessToken) {
        throw err;
      }
      console.error(err);
      // AB#72477 - a deactivated area on the logged user surfaces here as errors.AreaId
      // (see UserService#fetchUser). Rethrow it instead of swallowing to null so the
      // sign-in UI can show that specific reason instead of "invalid credentials".
      if ((err as Error & { errors?: Record<string, string[]> })?.errors?.['AreaId']) throw err;
      return null;
    }
  }

  public clearCognitoLocalStorage() {
    this._authSessionCleanupService.clear({ cognitoTokens: true });
  }

  public disconnectIdpSilently(): Promise<void> {
    return this._authCognitoService.disconnectIdpSilently();
  }

  public clearStoredAuthUser(userId?: string): void {
    this._authSessionCleanupService.clear({ storedAuthUsers: userId ?? true });
  }

  private async postLoginSubWithLiveSessionFallback(
    urlEnv: string,
    accessToken: string,
    ssoId: string,
    ssoUsername: string,
    allowLiveSessionFallback: boolean,
    liveSessionToken?: string,
    trustSub = allowLiveSessionFallback
  ): Promise<AcessSub> {
    try {
      return await this.postLoginSub(urlEnv, accessToken, ssoId, ssoUsername, trustSub);
    } catch (err) {
      if (
        allowLiveSessionFallback &&
        liveSessionToken &&
        liveSessionToken !== accessToken &&
        err instanceof HttpErrorResponse &&
        err.status === 400 &&
        err.error?.errors?.AccessToken
      ) {
        return this.postLoginSub(urlEnv, liveSessionToken, ssoId, ssoUsername, trustSub);
      }
      throw err;
    }
  }

  private postLoginSub(environmentUrl: string, accessToken: string, ssoId: string, ssoUsername?: string, trustSub = false): Promise<AcessSub> {
    const url = `${this._appConfig.APIs.apiUrlAuth}/Login/sub`;

    return lastValueFrom(
      this._httpClient.post<AcessSub>(url, {
        accessToken,
        // AB#71969 - sub is trusted server-side only for quick sign-in (LoginInternalCommandHandler).
        // Credentials/SSO login omits it, keeping strict AccessToken validation for that flow.
        ...(trustSub ? { sub: ssoId } : {}),
        ssoUserName: ssoUsername,
        environmentUrl,
      })
    )
      .then((r) => {
        this._authCognitoService.authenticatedUserSub = ssoId;
        return r;
      })
      .catch((err) => {
        // AB#69653 (revamp-Authentication PR #73) - /Login/sub returns 400 for three distinct
        // reasons, keyed differently: "UserNotFoundBySub" (sub genuinely doesn't match any stored
        // user - new user or a federated identity recreated by the IdP), "UserId" (user WAS found,
        // but writing LastLoginDate/access log failed), and "EnvironmentUrl" (bad environment). Only
        // the first one means "no such user" - treat every other 400 as a real, unclassified error
        // instead of silently mapping it to NotFoundUser (which would misroute an existing user to
        // the new-user flow over an unrelated backend hiccup).
        if (err instanceof HttpErrorResponse && err.status === 400 && err.error?.errors?.UserNotFoundBySub) return null;
        throw err;
      });
  }

  async getUserStatusAuth(): Promise<AuthStatus> {
    const fn = this.getAuthStatusFn;
    return lastValueFrom(
      (fn ?? (() => of(null)))().pipe(
        map((status) => {
          this._userStatusAuth.status = status;
          return status;
        })
      )
    );
  }

  /**
   * Sign in using the access token
   */
  signInUsingRefreshToken(): Observable<boolean> {
    if (environment.isLocalhost) return of(true);

    // Sign in using the token
    return this._tokenRefresh$.pipe(
      map((refreshedToken) => {
        // Replace the access token with the new one
        this.accessToken = refreshedToken;

        // Set the authenticated flag to true
        this._authenticated = true;

        applyLegacySessionCookies(refreshedToken);

        // Store the user permission
        this._permissionsService.loadPermissions(JSON.parse(localStorage.getItem('roles')));

        return true;
      }),
      catchError((err: any) => {
        console.error(err);
        return of(false);
      })
    );
  }

  /**
   * Replaces the access token with a fresh one before it expires. Leaves the last-access cookie alone,
   * so a background refresh never extends an idle session.
   */
  refreshAccessToken(): Observable<boolean> {
    return this._tokenRefresh$.pipe(
      map((refreshedToken) => {
        this.accessToken = refreshedToken;
        return true;
      }),
      catchError((err: unknown) => {
        console.warn(err);
        return of(false);
      })
    );
  }

  /**
   * Sign out
   *
   * @param forceClear wipe all local storage (besides the kept-users list and Amplify's own Cognito
   * session cache, both preserved across the wipe), not just the light local set. Amplify's cache is
   * kept here so the sign-in page can still resolve a federated session ("Continue as X" is
   * federated-only, AB#75346) - the remote session is still revoked via the silent REST call either
   * way. A deliberate full logout (user menu "Sair"/"Exit", expired-password's onRefresh) must call
   * clearCognitoLocalStorage() right after this, or the next direct login can hit Amplify's own
   * "There is already a signed in user." (AB#75346).
   * @param options.revokeRemoteSession when true (default), also revokes the Cognito session (silent
   * REST call, no browser navigation). Pass `false` for automatic/incidental sign-outs (idle timeout,
   * failed login attempt) where only OUR app's local session should end, to avoid cascading the
   * revoke to other apps sharing the same corporate SSO session.
   */
  signOut(forceClear?: boolean, options?: { revokeRemoteSession?: boolean }): Observable<any> {
    const revokeRemoteSession = options?.revokeRemoteSession ?? true;

    // Clear local storage
    if ((!forceClear && environment.isLocalhost) || this.isFeedModule) {
      this._authSessionCleanupService.clear({ accessToken: true, expiredTokenFlag: true });
    }

    this._authSessionCleanupService.clear({ legacyCookies: true });

    // Set the authenticated flag to false
    this._authenticated = false;

    if (typeof (window as any).AppLogoffCall != 'undefined') {
      (window as any).AppLogoffCall.postMessage('<function></function>');
    }

    this._signOut$.next({ success: true });

    const cognitoSignOut = !revokeRemoteSession
      ? Promise.resolve()
      : !this._authCognitoService.hasCognitoConfig()
        ? lastValueFrom(this.prepareConfigFromEnvironmentInfo()).then(() => this._authCognitoService.signOut())
        : this._authCognitoService.signOut();

    // Wipe the rest of local storage only AFTER the real Cognito/Amplify sign-out has been attempted.
    // Wiping it first (the previous order) removed the session tokens before Amplify could read them
    // to build its (global) sign-out/revoke request, so the remote session was never actually revoked
    // and could resurface - e.g. the "quick sign-in" user reappearing after a page refresh - even
    // though the local UI state had already reset. `finally` runs regardless of subscription, matching
    // the previous synchronous guarantee for fire-and-forget callers that never subscribe.
    const signOutSequence = cognitoSignOut.finally(() => {
      if (forceClear && !this.isFeedModule) {
        const users = this._storedAuthUserService.getUsersLogged();
        const cognitoEntries = this.#captureCognitoLocalStorage();
        localStorage.clear();
        this._storedAuthUserService.addUsers(users);
        cognitoEntries.forEach(([key, value]) => localStorage.setItem(key, value));
      }
    });

    return from(signOutSequence);
  }

  // AB#75346 - saves Amplify's local keys so forceClear's localStorage.clear() doesn't drop them.
  #captureCognitoLocalStorage(): [string, string][] {
    const entries: [string, string][] = [];
    Object.keys(localStorage).forEach((key) => {
      if (key?.startsWith('Cognito') || key?.startsWith('amplify-')) {
        const value = localStorage.getItem(key);
        if (value !== null) entries.push([key, value]);
      }
    });
    return entries;
  }

  /**
   * AB#75346 - centralizes the "deliberate full sign-out" composition duplicated identically across
   * user-grpd, user-old and user-waiting: revoke the remote session, then drop the stored quick
   * sign-in entry. Callers remain responsible for navigation and error handling, which differ per screen.
   */
  signOutAndClearUser(forceClear?: boolean): Observable<any> {
    return this.signOut(forceClear).pipe(tap(() => this.clearStoredAuthUser()));
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean> {
    // Check if the user is logged in
    if (this._authenticated) {
      return of(true);
    }

    // Check the access token availability
    if (!this.accessToken) {
      return of(false);
    }

    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.accessToken)) {
      return of(false);
    }

    return of(true);
  }

  async doValidatedUserRedirecting(user: User, userStatus: UserStatusAuth): Promise<void> {
    const status = userStatus.status;
    const fromAuthenticationApp = this._svcAppSettings.module === SvcModule.Authentication;
    const isContractorOrSupplierUser = [AuthStatus.SupplierUser, AuthStatus.ContractorUser].includes(status);
    const persistedMcpRedirect = takeMcpAuthorizationRedirect();
    const redirectURL =
      this._activatedRoute.snapshot.queryParamMap.get('redirectURL')
      ?? persistedMcpRedirect
      ?? this.#getDefaultPostSignInRedirectUrl();
    const redirectAsp = this._activatedRoute.snapshot.queryParamMap.get('redirectAsp');
    const redirectCore = this._activatedRoute.snapshot.queryParamMap.get('redirectCore');
    const needToRedirectToAsp = canRedirectToAspFromQueryParamData(redirectAsp);
    const needToRedirectToCore = canRedirectToCoreFromQueryParamData(redirectCore);
    const needToRedirectToAspOrCore = needToRedirectToAsp || needToRedirectToCore;
    const redirectToSupplierManagement = () => externalURLSamePageWithoutHistory(`${userStatus.user.baseUrl}/suppliermanagement`, ``);
    const redirectToContractorManagement = () => externalURLSamePageWithoutHistory(`${userStatus.user.baseUrl}/contractormanagement`, ``);
    const defaultRedirecting = () => {
      if (!tryToRedirectToAspFromQueryParamData(redirectAsp) && !tryToRedirectToCoreFromQueryParamData(redirectCore)) {
        // Navigate to the redirect url (angular to angular)
        this._router.navigateByUrl(redirectURL);
      }
    };

    if (status !== AuthStatus.SupplierUser && status !== AuthStatus.ContractorUser && status !== AuthStatus.EmployeeUser) {
      this._router.navigate(['/sign-out']);
      return;
    }

    // Generate legacy support cookies
    return this.signInLegacySupport(user, () => {
      if (!needToRedirectToAspOrCore && !isContractorOrSupplierUser && !fromAuthenticationApp) {
        defaultRedirecting();
      }
    })
      .then(() => {
        if (!needToRedirectToAspOrCore && !isContractorOrSupplierUser && !fromAuthenticationApp) return;

        if (status === AuthStatus.SupplierUser) return redirectToSupplierManagement();
        if (status === AuthStatus.ContractorUser) return redirectToContractorManagement();

        defaultRedirecting();
      });
  }

  syncLegacySessionCookies(): void {
    applyLegacySessionCookies(this.accessToken);
  }

  #getDefaultPostSignInRedirectUrl(): string {
    if (this._svcAppSettings.module === SvcModule.ExternalFeatures) {
      return '/user/settings';
    }
    return '/signed-in-redirect';
  }

  private async signInLegacySupport(user: User, afterMainCookiesCreated?: () => void): Promise<void> {
    //remove all old legacy cookies
    deleteLegacyCookies(['EE3B69BC-D275-4D89-ACCD-D91136DAD45C']);

    //TODO: remove this function when no legacy support needed (ASP + netcore)

    //environment
    updateEnvironmentFullURLCookie(this._environmentInfo.url);

    //user preferences
    updateLastSiteIdCookie(user.lastSiteId);
    updateLanguageIdCookie(user.languageId);
    updateUserIdCookie(this.userId);
    updateReloadSessionCookie('True');

    //product APIs
    updateProductAPITokenCookie(this.accessToken);
    updateBookmarkAPIUrlCookie(this._appConfig.APIs.apiUrlBookmark);
    updateDocumentAPIUrlCookie(this._appConfig.APIs.apiUrlDocuments);
    updateNotificationAPIUrlCookie(this._appConfig.APIs.apiUrlNotification);
    updateAuthenticationAPIUrlCookie(this._appConfig.APIs.apiUrlAuth);
    updateHashtagAPIUrlCookie(this._appConfig.APIs.apiUrlHashtag);
    updatePraiseAPIUrlCookie(this._appConfig.APIs.apiUrlPraise);
    updateReactionAPIUrlCookie(this._appConfig.APIs.apiUrlReaction);
    updateCommentsAPIUrlCookie(this._appConfig.APIs.apiUrlComments);
    updateActionPlanAPIUrlCookie(this._appConfig.APIs.apiUrlActionPlan);
    updateChecklistAPIUrlCookie(this._appConfig.APIs.apiUrlChecklist);
    updateCilAPIUrlCookie(this._appConfig.APIs.apiUrlCil);
    updateDefectTagAPIUrlCookie(this._appConfig.APIs.apiUrlDefectTag);
    updateAdministrationAPIUrlCookie(this._appConfig.APIs.apiUrlAdministration);
    updateCommentManagerAPIUrlCookie(this._appConfig.APIs.apiUrlCommentManager);
    updateMultilingualAPIUrlCookie(this._appConfig.APIs.apiUrlMultilingual);
    updateSubtitleAPIUrlCookie(this._appConfig.APIs.apiUrlSubtitle);
    updateCopilotAPIUrlCookie(this._appConfig.APIs.apiUrlCopilot);
    updateModuleIntegrationAPIUrlCookie(this._appConfig.APIs.apiUrlModuleIntegration);

    let cloudFront: CloudfrontCookie;

    await Promise.all([
      lastValueFrom(
        this._cloudfrontService.getSignedCookies().pipe(
          tap((signedCookies) => {
            cloudFront = signedCookies;
            //cloudfront signed cookies
            updateCloudFrontPolicyCookie(signedCookies.policy);
            updateCloudFrontSignatureCookie(signedCookies.signature);
            updateCloudFrontKeyPairIdCookie(signedCookies.keyPairId);
          })
        )
      ),
      lastValueFrom(
        this._parameterService.getAllAppGlobalParams().pipe(
          switchMap((params) => {
            //idle timeout controller
            const idleTimeout = params.find((item) => item.key === 'IDLE_TIMEOUT')?.value ?? '';
            updateIdleTimeoutCookie(idleTimeout);
            applyLegacySessionCookies(this.accessToken);

            //integration APIs
            const apiUrl = params.find((item) => item.key === 'API_URL')?.value ?? '';
            updateIntegrationAPIURLCookie(apiUrl);

            return (
              environment.isLocalhost || this.isFeedModule
                ? of({ token: 'EMPTY_VALUE' })
                : signinLegacyApi(this._httpClient, this._userStatusAuth.user.baseUrl, this._userStatusAuth.user.ssoId)
            ).pipe(
              tap((res) => {
                !environment.isLocalhost && updateIntegrationAPITokenCookie(res.token);
                afterMainCookiesCreated?.();
              })
            );
          })
        )
      ),
    ]);

    //cloudfront signed cookies (AGAIN)
    const createCloudFrontCookiesAgain = (): void => {
      updateCloudFrontPolicyCookie(cloudFront.policy);
      updateCloudFrontSignatureCookie(cloudFront.signature);
      updateCloudFrontKeyPairIdCookie(cloudFront.keyPairId);
    };
    const isInAuthentication = location.pathname.startsWith('/authentication');
    if (isInAuthentication) {
      await (async function (): Promise<void> {
        return new Promise((resolve) => {
          setTimeout(() => {
            createCloudFrontCookiesAgain();
            resolve();
          }, 1000);
        });
      })();
    } else {
      setTimeout(() => {
        createCloudFrontCookiesAgain();
      }, 2000);
    }
  }

  async checkUserExpiredPassword(): Promise<{ status: 'expired' | 'expiring' | null; days?: number }> {
    const date = await this.getPasswordChangeDate();
    if (!date) {
      this.setPasswordChangeDate();
      return { status: null };
    }
    const remainingDays = this.passwordExpirationDays - Math.ceil((new Date().getTime() - new Date(Number.parseInt(date) * 1000).getTime()) / (1000 * 60 * 60 * 24));
    return {
      status: remainingDays <= 10 && remainingDays > 0 ? 'expiring' : remainingDays <= 0 ? 'expired' : null,
      days: remainingDays,
    };
  }

  getExpiredToken() {
    return localStorage.getItem('expiredToken') === 'true';
  }

  setExpiredToken() {
    localStorage.setItem('expiredToken', 'true');
  }

  removeExpiredToken() {
    localStorage.removeItem('expiredToken');
  }

  async getPasswordChangeDate() {
    return await this._authCognitoService.getUserAttribute('custom:lastUpdatePassword');
  }

  async setPasswordChangeDate(): Promise<void> {
    await this._authCognitoService.setUserAttribute('custom:lastUpdatePassword', Math.floor(new Date().getTime() / 1000).toString());
  }

  async prepareCognitoFromEnvironment(): Promise<CognitoUser> {
    const env = await lastValueFrom(this.postEnvironmentInfo());
    this.applyCongnitoConfig(env);
    const user = await this.getCurrentUser();
    return user;
  }
}
